import { useState, FormEvent } from "react";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import BookCallModal from "../../components/feature/BookCallModal";

type SubmitState = "idle" | "submitting" | "success" | "error";

const businessTypes = [
  "Home Services",
  "Legal",
  "Medical / Medspa",
  "Financial Services",
  "Real Estate",
  "Other",
];

const leadVolumes = [
  "Under 20/month",
  "20–50/month",
  "50–100/month",
  "100–250/month",
  "250+/month",
];

const ContactPage = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [submitState, setSubmitState] = useState<SubmitState>("idle");
  const [charCount, setCharCount] = useState(0);
  const MAX_CHARS = 500;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem("message") as HTMLTextAreaElement)?.value || "";
    if (message.length > MAX_CHARS) return;

    setSubmitState("submitting");
    const formData = new FormData(form);
    const params = new URLSearchParams();
    formData.forEach((val, key) => params.append(key, val as string));

    try {
      const res = await fetch("https://readdy.ai/api/form/d71ccq3k1jkj467r5djg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      if (res.ok) {
        setSubmitState("success");
        form.reset();
        setCharCount(0);
      } else {
        setSubmitState("error");
      }
    } catch {
      setSubmitState("error");
    }
  };

  return (
    <div className="min-h-screen font-sans bg-white">
      <Navbar onBookCall={() => setModalOpen(true)} />

      {/* Page Hero */}
      <section className="relative bg-charcoal pt-40 pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/36b3116a2c893f452998df516030dbc5.jpg"
            alt=""
            className="w-full h-full object-cover object-top opacity-15"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-charcoal to-charcoal/80"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <div className="inline-block bg-sapphire/20 text-sapphire text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full mb-6">
            Contact
          </div>
          <h1 className="text-5xl lg:text-6xl font-extrabold text-white leading-tight max-w-2xl mb-5">
            Let&apos;s Build Your System
          </h1>
          <p className="text-white/55 text-lg leading-relaxed max-w-xl">
            Tell us about your business. We&apos;ll show you exactly where automation can increase your revenue.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-16 lg:gap-24">

            {/* Left — Form */}
            <div className="lg:col-span-3">
              <h2 className="text-xl font-bold text-charcoal mb-2">Request a Strategy Call</h2>
              <p className="text-sm text-gray-500 mb-8">We&apos;ll review your submission and reach out within 1 business day.</p>

              {submitState === "success" ? (
                <div className="bg-green-50 border border-green-200 rounded-xl p-10 text-center">
                  <div className="w-14 h-14 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                    <i className="ri-check-line text-green-600 text-2xl"></i>
                  </div>
                  <h3 className="text-lg font-bold text-charcoal mb-2">Request Received</h3>
                  <p className="text-sm text-gray-500">Thanks for reaching out. Our team will be in touch within 1 business day to schedule your strategy call.</p>
                  <button
                    onClick={() => setSubmitState("idle")}
                    className="mt-6 text-sm text-sapphire font-semibold hover:underline cursor-pointer"
                  >
                    Submit another request
                  </button>
                </div>
              ) : (
                <form
                  data-readdy-form
                  id="contact-strategy-call"
                  onSubmit={handleSubmit}
                  className="space-y-5"
                >
                  {/* Name + Email */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-semibold text-charcoal mb-1.5" htmlFor="name">
                        Full Name <span className="text-red-400">*</span>
                      </label>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        required
                        placeholder="Jane Smith"
                        className="w-full text-sm border border-gray-200 rounded-lg px-4 py-3 outline-none focus:border-sapphire focus:ring-1 focus:ring-sapphire/20 transition-all placeholder-gray-400"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-charcoal mb-1.5" htmlFor="email">
                        Email Address <span className="text-red-400">*</span>
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        placeholder="jane@company.com"
                        className="w-full text-sm border border-gray-200 rounded-lg px-4 py-3 outline-none focus:border-sapphire focus:ring-1 focus:ring-sapphire/20 transition-all placeholder-gray-400"
                      />
                    </div>
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-xs font-semibold text-charcoal mb-1.5" htmlFor="phone">
                      Phone Number <span className="text-red-400">*</span>
                    </label>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      required
                      placeholder="+1 (555) 000-0000"
                      className="w-full text-sm border border-gray-200 rounded-lg px-4 py-3 outline-none focus:border-sapphire focus:ring-1 focus:ring-sapphire/20 transition-all placeholder-gray-400"
                    />
                  </div>

                  {/* Business Type */}
                  <div>
                    <label className="block text-xs font-semibold text-charcoal mb-1.5" htmlFor="business_type">
                      Business Type <span className="text-red-400">*</span>
                    </label>
                    <select
                      id="business_type"
                      name="business_type"
                      required
                      defaultValue=""
                      className="w-full text-sm border border-gray-200 rounded-lg px-4 py-3 outline-none focus:border-sapphire focus:ring-1 focus:ring-sapphire/20 transition-all text-gray-700 cursor-pointer appearance-none bg-white"
                    >
                      <option value="" disabled>Select your industry</option>
                      {businessTypes.map((b) => (
                        <option key={b} value={b}>{b}</option>
                      ))}
                    </select>
                  </div>

                  {/* Monthly Lead Volume */}
                  <div>
                    <label className="block text-xs font-semibold text-charcoal mb-1.5" htmlFor="lead_volume">
                      Monthly Lead Volume
                    </label>
                    <select
                      id="lead_volume"
                      name="lead_volume"
                      defaultValue=""
                      className="w-full text-sm border border-gray-200 rounded-lg px-4 py-3 outline-none focus:border-sapphire focus:ring-1 focus:ring-sapphire/20 transition-all text-gray-700 cursor-pointer appearance-none bg-white"
                    >
                      <option value="" disabled>Estimate your monthly volume</option>
                      {leadVolumes.map((v) => (
                        <option key={v} value={v}>{v}</option>
                      ))}
                    </select>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-xs font-semibold text-charcoal mb-1.5" htmlFor="message">
                      Tell Us About Your Business
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={5}
                      maxLength={MAX_CHARS}
                      placeholder="Describe your current challenges, goals, or anything specific you want us to look at..."
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className="w-full text-sm border border-gray-200 rounded-lg px-4 py-3 outline-none focus:border-sapphire focus:ring-1 focus:ring-sapphire/20 transition-all placeholder-gray-400 resize-none"
                    />
                    <div className={`text-xs mt-1 text-right ${charCount > MAX_CHARS - 50 ? "text-red-400" : "text-gray-400"}`}>
                      {charCount}/{MAX_CHARS}
                    </div>
                  </div>

                  {submitState === "error" && (
                    <div className="bg-red-50 border border-red-200 text-red-600 text-xs rounded-lg px-4 py-3">
                      Something went wrong. Please try again or email us directly.
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={submitState === "submitting" || charCount > MAX_CHARS}
                    className="w-full bg-sapphire hover:bg-deep-sapphire disabled:opacity-60 text-white text-sm font-bold py-4 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {submitState === "submitting" ? "Sending..." : "Request Strategy Call"}
                  </button>
                </form>
              )}
            </div>

            {/* Right — Info Panel */}
            <div className="lg:col-span-2 space-y-8">
              {/* Direct booking */}
              <div className="bg-charcoal rounded-2xl p-7">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 flex items-center justify-center bg-sapphire/20 rounded-lg">
                    <i className="ri-calendar-check-line text-sapphire text-base"></i>
                  </div>
                  <span className="text-sm font-bold text-white">Or Book Directly</span>
                </div>
                <p className="text-xs text-white/50 mb-5 leading-relaxed">
                  Skip the form and pick a time that works for you. 30-minute strategy sessions, no pressure.
                </p>
                <button
                  onClick={() => setModalOpen(true)}
                  className="w-full bg-sapphire hover:bg-deep-sapphire text-white text-sm font-bold py-3.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  View Available Times
                </button>
              </div>

              {/* What to expect */}
              <div className="border border-gray-100 rounded-2xl p-7">
                <h3 className="text-sm font-bold text-charcoal mb-5">What Happens on the Call</h3>
                <ul className="space-y-4">
                  {[
                    { icon: "ri-map-pin-2-line", title: "Audit Your Current Setup", text: "We identify where leads are slipping through the cracks." },
                    { icon: "ri-blueprint-line", title: "Design Your System", text: "We outline the exact automation stack for your business." },
                    { icon: "ri-arrow-right-circle-line", title: "Clear Next Steps", text: "You leave knowing exactly what to build and what it will do." },
                  ].map((item) => (
                    <li key={item.title} className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center bg-sapphire/10 rounded-lg flex-shrink-0 mt-0.5">
                        <i className={`${item.icon} text-sapphire text-sm`}></i>
                      </div>
                      <div>
                        <p className="text-xs font-bold text-charcoal">{item.title}</p>
                        <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{item.text}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Contact info */}
              <div className="space-y-3">
                <a href="mailto:hello@bluedigitalai.com" rel="nofollow" className="flex items-center gap-3 group cursor-pointer">
                  <div className="w-9 h-9 flex items-center justify-center bg-sapphire/10 rounded-lg flex-shrink-0">
                    <i className="ri-mail-line text-sapphire text-sm"></i>
                  </div>
                  <span className="text-sm text-gray-600 group-hover:text-sapphire transition-colors">hello@bluedigitalai.com</span>
                </a>
                <a href="tel:+18005550199" rel="nofollow" className="flex items-center gap-3 group cursor-pointer">
                  <div className="w-9 h-9 flex items-center justify-center bg-sapphire/10 rounded-lg flex-shrink-0">
                    <i className="ri-phone-line text-sapphire text-sm"></i>
                  </div>
                  <span className="text-sm text-gray-600 group-hover:text-sapphire transition-colors">+1 (800) 555-0199</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer onBookCall={() => setModalOpen(true)} />
      <BookCallModal isOpen={modalOpen} onClose={() => setModalOpen(false)} />
    </div>
  );
};

export default ContactPage;
